import { computePosition, offset, shift, autoUpdate, flip } from "@floating-ui/dom";
import { usePromptContext } from "../../PromptContext";
import { createEffect, For, Match, on, onCleanup, Show, Switch } from "solid-js";
import { Portal } from "solid-js/web";
import { Spinner } from "@core/components/_original/Spinner";
import { StIcon } from "@core/components/icons";
import { TbCheck } from "solid-icons/tb";
import type { CollectionSnapshot } from "@core/domains/collections/collections.types";
import { getRootCollectionUIOverloads } from "@core/domains/collections/collections.helpers";
import { CollectionBreadcrumbs } from "@core/components/CollectionBreadcrumbs";
import { useWire } from "@core/wire";
import type { assets } from "@repo/client";
import { type FileTypeIcon, useFileTypeIcon } from "@core/ui/useFileTypeIcon";
import { originalFilenameToMimeType } from "@core/lib/originalFilenameToMimeType";

export const MentionsPickerPopup = () => {
  const { mentionsState } = usePromptContext();
  const state = mentionsState.mentionsState;
  let el!: HTMLDivElement;

  const updatePosition = (referenceEl: HTMLElement, floatingEl: HTMLElement) => {
    computePosition(referenceEl, floatingEl, {
      placement: "right-start",
      middleware: [offset(20), flip(), shift()],
    }).then((c) => {
      Object.assign(el.style, {
        left: `${c.x}px`,
        top: `${c.y}px`,
      });
    });
  };
  createEffect(
    on(
      () => state.domAnchor,
      (domAnchor) => {
        if (!domAnchor) return;
        const cleanup = autoUpdate(domAnchor, el, () => updatePosition(domAnchor, el));
        onCleanup(() => cleanup());
      },
    ),
  );

  // Close on outside click, even if overlays intercept
  createEffect(() => {
    if (!state.open) return;
    const handlePointerDown = (e: PointerEvent) => {
      // If el is not mounted yet, or click is inside the popup, do nothing
      if (!el || el.contains(e.target as Node)) return;
      mentionsState.dismiss();
      e.preventDefault();
      e.stopPropagation();
    };
    window.addEventListener("pointerdown", handlePointerDown, true); // use capture phase
    onCleanup(() => window.removeEventListener("pointerdown", handlePointerDown, true));
  });

  const collections = () => state.data.filter((d) => d.type === "collection");
  const assets = () => state.data.filter((d) => d.type === "asset");

  return (
    <Show when={state.open}>
      <Portal mount={document.body}>
        <div class="absolute inset-0" on:click={() => mentionsState.dismiss()} />
        <div
          ref={el}
          class="bg-background border border-background-decoration text-on-background rounded-lg shadow-lg absolute p-1 z-max max-h-[50vh] overflow-y-auto max-w-[80vw]"
          on:mousemove={() => state.lastInteraction !== "mouse" && mentionsState.setLastInteraction("mouse")}
        >
          <Switch>
            <Match when={state.loading}>
              <div class="px-2 py-1 bg-surface rounded text-on-background flex items-center gap-2 min-w-56">
                <Spinner class="size-4 text-on-background-alternate" />
                <span>Loading</span>
              </div>
            </Match>
            <Match when={state.error}>
              <button
                type="button"
                onClick={() => mentionsState.retry()}
                class="min-h-0 px-2 py-1 bg-surface rounded text-on-background flex items-center gap-2 min-w-56"
              >
                <p>
                  There was an error. <span class="font-medium">Retry?</span>
                </p>
                <StIcon icon={TbCheck} class="size-4 text-on-background-alternate" />
              </button>
            </Match>
            <Match when={state.data.length === 0}>
              <button
                type="button"
                onClick={() => mentionsState.dismiss()}
                class="min-h-0 px-2 py-1 bg-surface rounded text-on-background flex items-center gap-2 min-w-56"
              >
                <p>
                  No results found. <span class="font-medium">Dismiss?</span>
                </p>
                <StIcon icon={TbCheck} class="size-4 text-on-background-alternate" />
              </button>
            </Match>

            <Match when={state.data.length > 0}>
              <div class="flex flex-col gap-2">
                <Show when={collections().length > 0}>
                  <span class="text-on-background-variant text-sm select-none ml-2 mt-2">Collections</span>
                </Show>
                <For each={collections()}>
                  {(d, index) => (
                    <button
                      on:mouseenter={() => state.lastInteraction === "mouse" && mentionsState.setActiveIndex(index())}
                      onClick={() => mentionsState.select(index())}
                      class="min-h-0 rounded px-2 py-1 min-w-56"
                      classList={{
                        "bg-background-active": state.activeIndex === index(),
                      }}
                      type="button"
                    >
                      <MentionCollection
                        collection={d.data as CollectionSnapshot}
                        active={state.activeIndex === index()}
                      />
                    </button>
                  )}
                </For>

                <Show when={assets().length > 0}>
                  <span class="text-on-background-variant text-sm select-none ml-2 mt-2">Assets</span>
                </Show>
                <For each={assets()}>
                  {(d, index) => (
                    <button
                      on:mouseenter={() =>
                        state.lastInteraction === "mouse" &&
                        mentionsState.setActiveIndex(index() + collections().length)
                      }
                      onClick={() => mentionsState.select(index() + collections().length)}
                      class="min-h-0 rounded px-2 py-1 min-w-56"
                      classList={{
                        "bg-background-active": state.activeIndex === index() + collections().length,
                      }}
                      type="button"
                    >
                      <MentionAsset
                        asset={d.data as assets.AssetSnapshot}
                        collection={d.associatedCollection}
                        active={state.activeIndex === index() + collections().length}
                      />
                    </button>
                  )}
                </For>
              </div>
            </Match>
          </Switch>
        </div>
      </Portal>
    </Show>
  );
};

const MentionCollection = (props: {
  collection: CollectionSnapshot;
  active: boolean;
}) => {
  const wire = useWire();
  const override = () => getRootCollectionUIOverloads(props.collection);

  let ref!: HTMLDivElement;
  createEffect(() => {
    if (props.active) {
      ref.scrollIntoView({ behavior: "instant", block: "nearest" });
    }
  });
  return (
    <div ref={ref} class="flex items-center gap-2">
      <StIcon icon={override().icon} class="size-5 text-on-background-variant" />
      <div class="flex flex-col max-w-full overflow-hidden">
        <p class="font-medium truncate">{override().label}</p>

        <p class="[&_a]:min-h-6 [&_a]:min-w-max [&_span]:min-h-6 [&_span]:min-w-max [&_*]:!font-normal mt-2 opacity-60 text-sm pointer-events-none">
          <CollectionBreadcrumbs
            noNavigation
            max={5}
            breadcrumbs={wire.services.collections.getBreadcrumbs(props.collection.id) || []}
          />
        </p>
      </div>
    </div>
  );
};

const MentionAsset = (props: {
  asset: assets.AssetSnapshot;
  collection: string;
  active: boolean;
}) => {
  const wire = useWire();
  const iconTheme = () => useFileTypeIcon(originalFilenameToMimeType(props.asset.originalFilename) as FileTypeIcon);

  let ref!: HTMLDivElement;
  createEffect(() => {
    if (props.active) {
      ref.scrollIntoView({ behavior: "instant", block: "nearest" });
    }
  });

  return (
    <div ref={ref} class="flex items-center gap-2">
      <StIcon icon={iconTheme().icon} class="size-5 text-on-background-variant" />
      <div class="flex flex-col max-w-full overflow-hidden">
        <p class="font-medium truncate">
          {props.asset.originalFilename}{" "}
          <span class="text-on-background-variant text-xs ml-2">{props.asset.displayName}</span>
        </p>

        <p class="[&_a]:min-h-6 [&_a]:min-w-max [&_span]:min-h-6 [&_span]:min-w-max [&_*]:!font-normal mt-2 opacity-60 text-sm pointer-events-none">
          <CollectionBreadcrumbs
            noNavigation
            max={5}
            breadcrumbs={wire.services.collections.getBreadcrumbs(props.collection) || []}
          />
        </p>
      </div>
    </div>
  );
};
